import { Breadcrumbs, Link, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import useLocationHistory from "../../Hooks/useLocationHistory";

type ViewBreadcrumbsProps = {
  label: string | undefined;
  sx?: any;
} & React.HTMLAttributes<HTMLElement>;

function ViewBreadcrumbs({ label, sx, ...selectionProps }: ViewBreadcrumbsProps) {
  const location = useLocation();
  const { items, navigate, recordVisit, updateCurrentLabel } = useLocationHistory();

  // Sync current location with history on mount and location changes
  useEffect(() => {
    const currentPath = location.pathname;
    const lastItem = items[items.length - 1];

    // If current location is not in history, record it (without triggering router navigation)
    if (!lastItem || lastItem.path !== currentPath) {
      if (label) recordVisit(currentPath, label);
    } else if (lastItem.path === currentPath && lastItem.label !== label) {
      // If path matches but label is different, update the label
      if (label) updateCurrentLabel(label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, label]);

  const displayItems = useMemo(() => items, [items]);

  return (
    <Breadcrumbs separator="›" aria-label="breadcrumbs" sx={sx} {...selectionProps}>
      {displayItems.map((item, index) => {
        const isLast = index === displayItems.length - 1;

        if (isLast) {
          return (
            <Typography key={item.path} sx={{ color: "text.primary" }}>
              {item.label}
            </Typography>
          );
        }

        return (
          <Link
            key={item.path}
            href={item.path}
            underline="hover"
            color="inherit"
            onClick={(e) => {
              e.preventDefault();
              navigate(item.path, item.label);
            }}
          >
            {item.label}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
}

export default ViewBreadcrumbs;
